package com.evoapps.evoshift.domain.model

import com.evoapps.evoshift.domain.market.payroll.WorkedSpan
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.plus
import kotlinx.serialization.Serializable
import kotlin.time.Duration.Companion.minutes

/**
 * SH-116: one break taken while clocked in, stored in `shifts/{id}.actual.breaks[]`. Times are the
 * workplace's local ISO date-times to the minute, like the clock times; [end] is empty while the
 * break goes on. [source] says where it was started (phone or watch: APP, the tablet: KIOSK, the
 * tablet's code: QR, a manager's correction: MANAGER).
 */
@Serializable
data class ShiftBreak(
    val start: String,
    val end: String = "",
    val source: ClockSource? = null,
    /** The geofence check of each end, where the workplace has one (SH-106; never coordinates). */
    val startLocationCheck: LocationCheck? = null,
    val startDistanceM: Int? = null,
    val endLocationCheck: LocationCheck? = null,
    val endDistanceM: Int? = null
) {
    val isOpen: Boolean get() = end.isEmpty()
}

/**
 * SH-116: `workplaces/{id}.settings.breakPolicy`. Which break time is paid work and when to remind
 * the person to take one. Defaults change nothing: the paid break is the law, an unpaid break and
 * the reminder are off.
 */
@Serializable
data class BreakPolicy(
    /**
     * The paid breaks of Kodeks pracy art. 134 § 1 (break time that counts as work, [paidBreakMinutesFor]):
     * [PAID_BREAK_MINUTES] when the working day is at least [PAID_BREAK_MIN_WORK_MINUTES], another
     * when it is longer than [SECOND_PAID_BREAK_AFTER_MINUTES] and a third when it is longer than
     * [THIRD_PAID_BREAK_AFTER_MINUTES] (points 2 and 3 added by the act of 9 March 2023,
     * Dz.U. 2023 poz. 641, in force since 26 April 2023).
     */
    val paidBreak: Boolean = true,
    /**
     * An unpaid break of up to this many minutes (art. 141, set in the work regulations): break
     * time beyond the paid one, up to this, is taken off the hours worked and the labour cost.
     * 0: no unpaid break, all break time is paid.
     */
    val unpaidBreakMinutes: Int = 0,
    /** Remind the person after this many hours clocked in without a break; 0 = off. */
    val reminderAfterHours: Int = 0
) {
    companion object {
        /** Each paid break of art. 134 § 1 KP: at least 15 minutes. */
        const val PAID_BREAK_MINUTES = 15
        /** Art. 134 § 1 pkt 1 KP: a working day of at least 6 hours has the first paid break. */
        const val PAID_BREAK_MIN_WORK_MINUTES = 6 * 60
        /** Art. 134 § 1 pkt 2 KP (since 26 April 2023): longer than 9 hours, a second one. */
        const val SECOND_PAID_BREAK_AFTER_MINUTES = 9 * 60
        /** Art. 134 § 1 pkt 3 KP (since 26 April 2023): longer than 16 hours, a third one. */
        const val THIRD_PAID_BREAK_AFTER_MINUTES = 16 * 60

        /**
         * SH-136: the paid break time of art. 134 § 1 KP for a working day of [workMinutes]: 15 minutes
         * from 6 hours, 30 over 9 hours, 45 over 16 hours. The law speaks of the daily working time
         * ("dobowy wymiar czasu pracy") without saying whether planned or worked; like the first
         * break since SH-116 we measure the clocked shift, so overtime that makes the day longer
         * also earns the next break. Mirrored in functions/breaks.js and functions/workingTime.js.
         */
        fun paidBreakMinutesFor(workMinutes: Long): Int = when {
            workMinutes > THIRD_PAID_BREAK_AFTER_MINUTES -> 3 * PAID_BREAK_MINUTES
            workMinutes > SECOND_PAID_BREAK_AFTER_MINUTES -> 2 * PAID_BREAK_MINUTES
            workMinutes >= PAID_BREAK_MIN_WORK_MINUTES -> PAID_BREAK_MINUTES
            else -> 0
        }
        const val MAX_UNPAID_BREAK_MINUTES = 60
        const val MAX_REMINDER_HOURS = 12

        /** The choices offered in the workplace settings; 0 = off. */
        val UNPAID_BREAK_CHOICES = listOf(0, 15, 30, 45, 60)
        val REMINDER_CHOICES = listOf(0, 3, 4, 5, 6)

        /** Tolerant read of stored values (out of range: the nearest allowed, negative: off). */
        fun of(paidBreak: Boolean?, unpaidBreakMinutes: Int?, reminderAfterHours: Int?) = BreakPolicy(
            paidBreak = paidBreak ?: true,
            unpaidBreakMinutes = (unpaidBreakMinutes ?: 0).coerceIn(0, MAX_UNPAID_BREAK_MINUTES),
            reminderAfterHours = (reminderAfterHours ?: 0).coerceIn(0, MAX_REMINDER_HOURS)
        )
    }
}

/**
 * SH-116: how breaks count. Shared by every hours and cost calculation (labour cost, the daily
 * log, payroll and working-time reports, the employee's estimate), so they all agree:
 *
 * - Breaks are clipped to the clocked times and merged where they overlap; a break still open
 *   when the shift was clocked out ends at the clock-out.
 * - The first [BreakPolicy.PAID_BREAK_MINUTES] of break time are paid when the policy has the paid
 *   break and the clocked shift is at least [BreakPolicy.PAID_BREAK_MIN_WORK_MINUTES] long; SH-136:
 *   30 minutes when it is longer than 9 hours and 45 when longer than 16 (art. 134 § 1 KP).
 * - The break time after that, up to [BreakPolicy.unpaidBreakMinutes], is unpaid: it is not worked
 *   time. Anything beyond both stays paid (the law caps the unpaid break; the manager corrects).
 */
object BreakRules {
    /** The break going on now, if any. */
    fun open(breaks: List<ShiftBreak>): ShiftBreak? = breaks.lastOrNull { it.isOpen }

    /** The breaks within [start, end), merged, in order; open ones end at [end]. */
    fun intervals(breaks: List<ShiftBreak>, start: LocalDateTime, end: LocalDateTime): List<WorkedSpan> {
        val clipped = breaks.mapNotNull { b ->
            val from = parse(b.start) ?: return@mapNotNull null
            val to = if (b.isOpen) end else parse(b.end) ?: return@mapNotNull null
            val s = maxOf(from, start)
            val e = minOf(to, end)
            if (e > s) WorkedSpan(s, e) else null
        }.sortedBy { it.start }
        val merged = mutableListOf<WorkedSpan>()
        clipped.forEach { span ->
            val last = merged.lastOrNull()
            if (last != null && span.start <= last.end) merged[merged.lastIndex] = last.copy(end = maxOf(last.end, span.end))
            else merged += span
        }
        return merged
    }

    /** Minutes of the breaks already over (as recorded, before any clipping). */
    fun endedMinutes(breaks: List<ShiftBreak>): Long = breaks.sumOf { b ->
        val s = parse(b.start)
        val e = parse(b.end)
        if (s != null && e != null && e > s) minutesBetween(s, e) else 0L
    }

    /** All break minutes within the clocked times. */
    fun breakMinutes(breaks: List<ShiftBreak>, start: LocalDateTime, end: LocalDateTime): Long =
        intervals(breaks, start, end).sumOf { minutesBetween(it.start, it.end) }

    /** The paid break time [policy] grants a clocked shift from [start] to [end] (SH-136: 15, 30 or 45 minutes). */
    fun paidAllowanceMinutes(start: LocalDateTime, end: LocalDateTime, policy: BreakPolicy): Long =
        if (policy.paidBreak) BreakPolicy.paidBreakMinutesFor(minutesBetween(start, end)).toLong() else 0L

    /** The stretches of break that are not paid, in order (see the rules above). */
    fun unpaidIntervals(breaks: List<ShiftBreak>, start: LocalDateTime, end: LocalDateTime, policy: BreakPolicy): List<WorkedSpan> {
        if (policy.unpaidBreakMinutes <= 0 || end <= start) return emptyList()
        var paidLeft = paidAllowanceMinutes(start, end, policy)
        var unpaidLeft = policy.unpaidBreakMinutes.toLong()
        val unpaid = mutableListOf<WorkedSpan>()
        for (span in intervals(breaks, start, end)) {
            if (unpaidLeft <= 0) break
            var from = span.start
            val length = minutesBetween(span.start, span.end)
            val paid = minOf(paidLeft, length)
            paidLeft -= paid
            from = from.plusMinutes(paid)
            val rest = minOf(length - paid, unpaidLeft)
            if (rest > 0) {
                unpaid += WorkedSpan(from, from.plusMinutes(rest))
                unpaidLeft -= rest
            }
        }
        return unpaid
    }

    fun unpaidMinutes(breaks: List<ShiftBreak>, start: LocalDateTime, end: LocalDateTime, policy: BreakPolicy): Long =
        unpaidIntervals(breaks, start, end, policy).sumOf { minutesBetween(it.start, it.end) }

    /** Clocked minutes less the unpaid break. */
    fun workedMinutes(breaks: List<ShiftBreak>, start: LocalDateTime, end: LocalDateTime, policy: BreakPolicy): Long =
        (minutesBetween(start, end) - unpaidMinutes(breaks, start, end, policy)).coerceAtLeast(0)

    /**
     * Whether to remind the person at [now]: the policy reminds, they are clocked in since
     * [clockedIn], not on a break, and their last break (or the clock-in) is at least
     * [BreakPolicy.reminderAfterHours] ago. Mirrors functions/breaks.js.
     */
    fun reminderDue(breaks: List<ShiftBreak>, clockedIn: LocalDateTime, now: LocalDateTime, policy: BreakPolicy): Boolean {
        if (policy.reminderAfterHours <= 0 || open(breaks) != null) return false
        val since = breaks.mapNotNull { parse(it.end) }.filter { it >= clockedIn }.maxOrNull() ?: clockedIn
        return minutesBetween(since, now) >= policy.reminderAfterHours * 60L
    }

    /** Checks a manager's corrected breaks against the clocked times; null when they are fine. */
    fun validate(breaks: List<ShiftBreak>, actualStart: String, actualEnd: String): BreakProblem? {
        val start = parse(actualStart)
        val end = parse(actualEnd)
        var previousEnd: LocalDateTime? = null
        breaks.sortedBy { it.start }.forEachIndexed { index, b ->
            val s = parse(b.start) ?: return BreakProblem.UNREADABLE
            val e = if (b.isOpen) null else parse(b.end) ?: return BreakProblem.UNREADABLE
            if (b.isOpen && (end != null || index != breaks.lastIndex)) return BreakProblem.OPEN_AFTER_CLOCK_OUT
            if (e != null && e <= s) return BreakProblem.ENDS_BEFORE_START
            if (start == null || s < start || (end != null && (e ?: s) > end)) return BreakProblem.OUTSIDE_SHIFT
            if (previousEnd != null && s < previousEnd!!) return BreakProblem.OVERLAP
            previousEnd = e ?: s
        }
        return if (breaks.size > MAX_BREAKS) BreakProblem.TOO_MANY else null
    }

    /**
     * SH-116: a clock time ("HH:mm") of a break on a shift clocked in at [actualStart] (local ISO):
     * on the clock-in's day, or the next day when earlier than the clock-in (overnight shifts).
     */
    fun atClock(actualStart: String, hhmm: String): String? {
        val start = parse(actualStart) ?: return null
        val time = runCatching { kotlinx.datetime.LocalTime.parse(hhmm) }.getOrNull() ?: return null
        val sameDay = LocalDateTime(start.date, time)
        val at = if (sameDay < LocalDateTime(start.date, kotlinx.datetime.LocalTime(start.hour, start.minute))) {
            LocalDateTime(start.date.plus(kotlinx.datetime.DatePeriod(days = 1)), time)
        } else sameDay
        return at.toString().let { if (it.length == 16) "$it:00" else it }
    }

    /** At most this many breaks per shift (the server refuses more). */
    const val MAX_BREAKS = 10

    internal fun parse(value: String): LocalDateTime? =
        value.takeIf { it.length >= 16 }?.let { runCatching { LocalDateTime.parse(if (it.length == 16) "$it:00" else it.take(19)) }.getOrNull() }

    internal fun minutesBetween(from: LocalDateTime, to: LocalDateTime): Long =
        (to.toInstant(TimeZone.UTC) - from.toInstant(TimeZone.UTC)).inWholeMinutes

    private fun LocalDateTime.plusMinutes(minutes: Long): LocalDateTime {
        return (toInstant(TimeZone.UTC) + minutes.minutes).toLocalDateTime(TimeZone.UTC)
    }
}

/** Why a manager's break correction cannot be saved (the screen words it). */
enum class BreakProblem { UNREADABLE, ENDS_BEFORE_START, OUTSIDE_SHIFT, OVERLAP, OPEN_AFTER_CLOCK_OUT, TOO_MANY }

class BreakException(val problem: BreakProblem) : Exception("Break: $problem")

/** The clocked span of a shift that counts for pay (SH-134 [payableTimes]), when clocked in and out. */
private fun Shift.clockedSpan(clockReview: ClockReviewSettings = ClockReviewSettings()): Pair<LocalDateTime, LocalDateTime>? {
    val actual = payableTimes(clockReview) ?: return null
    val start = BreakRules.parse(actual.start) ?: return null
    val end = BreakRules.parse(actual.end) ?: return null
    return if (end > start) start to end else null
}

/** SH-116: the unpaid break stretches of a clocked-out shift under [policy]. */
fun Shift.unpaidBreaks(policy: BreakPolicy, clockReview: ClockReviewSettings = ClockReviewSettings()): List<WorkedSpan> =
    clockedSpan(clockReview)?.let { (start, end) -> BreakRules.unpaidIntervals(breaks, start, end, policy) }.orEmpty()

/**
 * SH-116: hours worked of a clocked-out shift (clocked time less the unpaid break); null when not
 * clocked out. SH-134: of the payable times under [clockReview].
 */
fun Shift.workedHours(policy: BreakPolicy, clockReview: ClockReviewSettings = ClockReviewSettings()): Double? =
    clockedSpan(clockReview)?.let { (start, end) -> BreakRules.workedMinutes(breaks, start, end, policy) / 60.0 }

/** SH-116: break minutes of a clocked-out shift, paid and unpaid. */
fun Shift.breakMinutes(): Long = clockedSpan()?.let { (start, end) -> BreakRules.breakMinutes(breaks, start, end) } ?: 0L
